import { CloseIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tag,
  TagLabel,
  TagLeftIcon,
  TagRightIcon,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { stringify } from 'query-string'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsTags } from 'react-icons/bs'
import { GoTag } from 'react-icons/go'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import AWSIcon from '../../../../../graphics/AWSIcon'
import GoogleIcon from '../../../../../graphics/GoogleIcon'
import { checkOperationPermission, PermissionsEnum } from '../../../../../utils'
import {
  allowExtensionOperations,
  canAddTags,
  encodeStringForUrl,
} from '../../../../../utils/mediaLibrary'
import { ACLPermissionEnum } from '../../../../constants'
import type {
  FileDetailsResponse,
  SearchQueryJson,
} from '../../../../interfaces'
import { MLContext, UserContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import PermissionGuard from '../../../PermissionGuard'
import AddTagsModal from '../AddTagsModal'
import AutoTagsModal from '../AutoTagsModal'

interface TagsProps {
  fileDetails: FileDetailsResponse
  isTagsModalOpen: boolean
  toggleTagsModal(open: boolean): void
  onPatched(patch: Partial<FileDetailsResponse>): void
}

const Tags: React.FC<TagsProps> = ({
  fileDetails,
  isTagsModalOpen,
  toggleTagsModal,
  onPatched,
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [, dispatch] = useContext(MLContext)
  const [isAutoTagsModalOpen, toggleAutoTagsModal] = useState(false)
  const routerHistory = useHistory()

  const { request } = useFetch({ cachePolicy: CachePolicies.NO_CACHE })

  const deleteAITag = async (
    fileId: string,
    tagToRemove: string,
    onClose: () => void,
  ): Promise<void> => {
    const response = await request.post('/api/v2/files/removeAITags', {
      fileIds: [fileId],
      AITags: [tagToRemove],
    })

    if (response.successfullyUpdatedFileIds?.length > 0) {
      const nextAITags = (fileDetails.AITags ?? []).filter(
        tag => tag.name !== tagToRemove,
      )
      const updated = { ...fileDetails, AITags: nextAITags }
      dispatch({ type: MediaLibraryActions.UPDATE_FILES, payload: [updated] })
      onPatched({ AITags: nextAITags })
    }
    onClose()
  }

  const removeTags = async ({
    fileId,
    tagToRemove,
    onClose,
  }: {
    fileId: string
    tagToRemove: string
    onClose(): void
  }): Promise<void> => {
    const response = await request.post('/api/v2/files/removeTags', {
      fileIds: [fileId],
      tags: [tagToRemove],
    })

    if (response.successfullyUpdatedFileIds?.length > 0) {
      const nextTags = (fileDetails.tags ?? []).filter(
        tag => tag !== tagToRemove,
      )
      const updated = { ...fileDetails, tags: nextTags }
      dispatch({ type: MediaLibraryActions.UPDATE_FILES, payload: [updated] })
      onPatched({ tags: nextTags })
    }
    onClose()
  }

  return (
    <PermissionGuard
      permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW}
    >
      {hasPermission =>
        hasPermission ? (
          <Box>
            <Text fontWeight="600" mb="12px">
              {t('tags', 'Tags')}
            </Text>

            {/* Buttons */}
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {hasTagsWritePermission =>
                hasTagsWritePermission ? (
                  <Flex
                    mb={
                      fileDetails.tags?.length || fileDetails.AITags?.length
                        ? '20px'
                        : '0px'
                    }
                    gap="24px"
                  >
                    {canAddTags(fileDetails) &&
                      checkOperationPermission({
                        user,
                        aclPermission: [
                          ACLPermissionEnum.CONTRIBUTE,
                          ACLPermissionEnum.MANAGE,
                        ],
                        selectedFiles: [fileDetails],
                        selectedFolders: [],
                        selectedMediaCollections: [],
                      }) && (
                        <Button
                          variant="link"
                          size="sm"
                          iconSpacing="4px"
                          leftIcon={<GoTag />}
                          fontWeight="500"
                          fontSize="14px"
                          onClick={() => toggleTagsModal(true)}
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {t('edit_tags', 'Edit tags')}
                        </Button>
                      )}

                    {allowExtensionOperations(fileDetails) &&
                      checkOperationPermission({
                        user,
                        aclPermission: [
                          ACLPermissionEnum.CONTRIBUTE,
                          ACLPermissionEnum.MANAGE,
                        ],
                        selectedFiles: [fileDetails],
                        selectedFolders: [],
                        selectedMediaCollections: [],
                      }) && (
                        <Button
                          variant="link"
                          size="sm"
                          iconSpacing="4px"
                          leftIcon={<BsTags />}
                          fontWeight="500"
                          fontSize="14px"
                          onClick={() => toggleAutoTagsModal(true)}
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {t('add_auto_tags', 'Add auto tags')}
                        </Button>
                      )}
                  </Flex>
                ) : (
                  <></>
                )
              }
            </PermissionGuard>

            {/* Tag list */}
            <Flex width="full" flexWrap="wrap">
              {(fileDetails.tags ?? []).map(item => (
                <Tooltip
                  label={t('search_for_tag', 'Search for tag')}
                  placement="bottom"
                  key={item}
                >
                  <Tag
                    variant="outline"
                    boxShadow="inset 0 0 0px 1px #E6E7EA"
                    fontWeight="500"
                    height="32px"
                    display="inline-flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap="5px"
                    size="lg"
                    marginRight="5px"
                    marginBottom="10px"
                    rounded="50"
                    bg="#FFFFFF"
                    color="#3B485E"
                    _hover={{
                      bg: '#E6EFFF',
                      textColor: '#0450D5',
                      cursor: 'pointer',
                      boxShadow: 'inset 0 0 0px 1px #E6EFFF',
                    }}
                    onClick={() => {
                      const searchQuery = `(tags IN ["${item}"])`,
                        searchQueryJson = encodeStringForUrl(
                          JSON.stringify({
                            boolean: 'and',
                            filterGroups: [
                              {
                                boolean: 'and',
                                expressions: [
                                  {
                                    fieldName: 'tags',
                                    operator: 'IS_ONE_OF',
                                    value: [item],
                                  },
                                ],
                              },
                            ],
                          } as SearchQueryJson),
                        )
                      routerHistory?.push({
                        pathname: '/media-library',
                        search: stringify(
                          {
                            searchQuery,
                            searchQueryJson,
                          },
                          { skipNull: true },
                        ),
                      })
                    }}
                  >
                    <TagLabel lineHeight="1.3125" wordBreak="break-word">
                      {item}
                    </TagLabel>
                    <PermissionGuard
                      permission={
                        PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                      }
                    >
                      {(hasTagsDeletePermission: boolean) =>
                        hasTagsDeletePermission &&
                        canAddTags(fileDetails) &&
                        checkOperationPermission({
                          user,
                          aclPermission: [
                            ACLPermissionEnum.CONTRIBUTE,
                            ACLPermissionEnum.MANAGE,
                          ],
                          selectedFiles: [fileDetails],
                          selectedFolders: [],
                          selectedMediaCollections: [],
                        }) ? (
                          <Popover>
                            {({ onClose }) => (
                              <>
                                <PopoverTrigger>
                                  <Box
                                    position="relative"
                                    width="24px"
                                    height="24px"
                                    marginRight="-5px"
                                    borderRadius="32"
                                    cursor="pointer"
                                    overflow="hidden"
                                    onClick={event => {
                                      event.stopPropagation()
                                    }}
                                    textColor={'#4A5568'}
                                    _hover={{
                                      bg: '#cddcf7',
                                      textColor: '#1A202C',
                                    }}
                                  >
                                    <TagRightIcon
                                      as={CloseIcon}
                                      width="8.95px"
                                      height="8.95px"
                                      marginLeft="7.52px"
                                      marginTop="7.53px"
                                      position="absolute"
                                    />
                                  </Box>
                                </PopoverTrigger>
                                <PopoverContent
                                  onClick={event => {
                                    event.stopPropagation()
                                  }}
                                >
                                  <PopoverArrow />
                                  <PopoverCloseButton color="appSlate" />
                                  <PopoverHeader color="appSlate">
                                    {t('delete_tag', 'Delete tag')}
                                  </PopoverHeader>
                                  <PopoverBody color="appSlate">
                                    <VStack
                                      spacing="4"
                                      justifyContent="flex-start"
                                      width="full"
                                      alignItems="flex-start"
                                    >
                                      <Text>
                                        {t(
                                          'delete_confirmation_message',
                                          'Are you sure you want to delete?',
                                        )}
                                      </Text>
                                      <Button
                                        colorScheme="appBrick"
                                        variant="solid"
                                        onClick={() => {
                                          void removeTags({
                                            fileId: fileDetails.fileId,
                                            tagToRemove: item,
                                            onClose,
                                          })
                                        }}
                                      >
                                        {t('confirm', 'Confirm')}
                                      </Button>
                                    </VStack>
                                  </PopoverBody>
                                </PopoverContent>
                              </>
                            )}
                          </Popover>
                        ) : (
                          <></>
                        )
                      }
                    </PermissionGuard>
                  </Tag>
                </Tooltip>
              ))}
              {(fileDetails.AITags ?? []).map(item => (
                <Tooltip
                  label={t('search_for_tag', 'Search for tag')}
                  placement="bottom"
                >
                  <Tag
                    key={item.name}
                    variant="outline"
                    boxShadow="inset 0 0 0px 1px #E6E7EA"
                    fontWeight="500"
                    height="32px"
                    display="inline-flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap="5px"
                    size="lg"
                    marginRight="5px"
                    marginBottom="10px"
                    rounded="50"
                    bg="#FFFFFF"
                    color="#3B485E"
                    _hover={{
                      bg: '#E6EFFF',
                      textColor: '#0450D5',
                      cursor: 'pointer',
                      boxShadow: 'inset 0 0 0px 1px #E6EFFF',
                    }}
                    onClick={() => {
                      const searchQuery = `(tags IN ["${item.name}"])`,
                        searchQueryJson = encodeStringForUrl(
                          JSON.stringify({
                            boolean: 'and',
                            filterGroups: [
                              {
                                boolean: 'and',
                                expressions: [
                                  {
                                    fieldName: 'tags',
                                    operator: 'IS_ONE_OF',
                                    value: [item.name],
                                  },
                                ],
                              },
                            ],
                          } as SearchQueryJson),
                        )
                      routerHistory?.push({
                        pathname: '/media-library',
                        search: stringify(
                          {
                            searchQuery,
                            searchQueryJson,
                          },
                          { skipNull: true },
                        ),
                      })
                    }}
                  >
                    <TagLeftIcon
                      as={
                        item.source === 'google-auto-tagging'
                          ? GoogleIcon
                          : AWSIcon
                      }
                      background="white"
                      borderRadius="full"
                      padding="2px"
                      boxSize="1.25rem"
                      marginRight="0px"
                    />
                    <TagLabel lineHeight="1.3125" wordBreak="break-word">
                      {item.name}
                    </TagLabel>
                    <PermissionGuard
                      permission={
                        PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                      }
                    >
                      {(hasTagsDeletePermission: boolean) =>
                        hasTagsDeletePermission &&
                        canAddTags(fileDetails) &&
                        checkOperationPermission({
                          user,
                          aclPermission: [
                            ACLPermissionEnum.CONTRIBUTE,
                            ACLPermissionEnum.MANAGE,
                          ],
                          selectedFiles: [fileDetails],
                          selectedFolders: [],
                          selectedMediaCollections: [],
                        }) ? (
                          <Popover>
                            {({ onClose }) => (
                              <>
                                <PopoverTrigger>
                                  <Box
                                    position="relative"
                                    width="24px"
                                    height="24px"
                                    marginRight="-5px"
                                    borderRadius="32"
                                    cursor="pointer"
                                    overflow="hidden"
                                    onClick={event => {
                                      event.stopPropagation()
                                    }}
                                    textColor={'#4A5568'}
                                    _hover={{
                                      bg: '#cddcf7',
                                      textColor: '#1A202C',
                                    }}
                                  >
                                    <TagRightIcon
                                      as={CloseIcon}
                                      width="8.95px"
                                      height="8.95px"
                                      marginLeft="7.52px"
                                      marginTop="7.53px"
                                      position="absolute"
                                    />
                                  </Box>
                                </PopoverTrigger>
                                <PopoverContent
                                  onClick={event => {
                                    event.stopPropagation()
                                  }}
                                >
                                  <PopoverArrow />
                                  <PopoverCloseButton color="appSlate" />
                                  <PopoverHeader color="appSlate">
                                    {t('delete_ai_tag', 'Delete AI tag')}
                                  </PopoverHeader>
                                  <PopoverBody color="appSlate">
                                    <VStack
                                      spacing="4"
                                      justifyContent="flex-start"
                                      width="full"
                                      alignItems="flex-start"
                                    >
                                      <Text>
                                        {t(
                                          'delete_confirmation_message',
                                          'Are you sure you want to delete?',
                                        )}
                                      </Text>
                                      <Button
                                        colorScheme="appBrick"
                                        variant="solid"
                                        onClick={() => {
                                          void deleteAITag(
                                            fileDetails.fileId,
                                            item.name,
                                            onClose,
                                          )
                                        }}
                                      >
                                        {t('confirm', 'Confirm')}
                                      </Button>
                                    </VStack>
                                  </PopoverBody>
                                </PopoverContent>
                              </>
                            )}
                          </Popover>
                        ) : (
                          <></>
                        )
                      }
                    </PermissionGuard>
                  </Tag>
                </Tooltip>
              ))}
            </Flex>

            {/* Modals */}
            <AddTagsModal
              fileDetails={fileDetails}
              data={fileDetails.tags ?? []}
              isOpen={isTagsModalOpen}
              onClose={() => void toggleTagsModal(false)}
              onPatchedTags={nextTags => {
                const updated = { ...fileDetails, tags: nextTags }
                dispatch({
                  type: MediaLibraryActions.UPDATE_FILES,
                  payload: [updated],
                })
                onPatched({ tags: nextTags })
              }}
            />
            <AutoTagsModal
              fileId={fileDetails.fileId}
              isOpen={isAutoTagsModalOpen}
              onClose={() => void toggleAutoTagsModal(false)}
              onPatchedDetails={patch => {
                const updated = { ...fileDetails, ...patch }
                dispatch({
                  type: MediaLibraryActions.UPDATE_FILES,
                  payload: [updated],
                })
                onPatched(patch)
              }}
            />
          </Box>
        ) : (
          <></>
        )
      }
    </PermissionGuard>
  )
}

export default Tags
